import { mxConstants, mxEvent, mxGraph, mxGraphHandler, mxPoint, mxUtils, mxVertexHandler } from "./mxModule";

const graphPropertiesForSymbol = (container, graph) => {
  // Default style
  const style = graph.getStylesheet().getDefaultVertexStyle();
  style[mxConstants.STYLE_ROUNDED] = false;
  style[mxConstants.STYLE_FILLCOLOR] = "#ffffff";
  style[mxConstants.STYLE_STROKECOLOR] = "#000000";
  style[mxConstants.STYLE_STROKEWIDTH] = "2";
  style[mxConstants.STYLE_FONTCOLOR] = "#000000";
  style[mxConstants.STYLE_FONTSIZE] = "12";
  style[mxConstants.STYLE_FONTSTYLE] = 1;
  graph.getStylesheet().putDefaultVertexStyle(style);
  graph.setHtmlLabels(true);
  graph.isCellEditable = function(cell) {
    // Block HTML labels from being edited
    const cellValue = this.getModel().getValue(cell);
    if (typeof cellValue === 'string' && cellValue.includes('<')) {
      return false;
    }
    
    // Allow editing for non-HTML content
    return true;
  };

  mxEvent.disableContextMenu(container);
  graph.setConnectable(false);
  // Fold disabling
  graph.isCellFoldable = function (cell, collapse) {
    const childCount = this.model.getChildCount(cell);
    for (let i = 0; i < childCount; i++) {
      const child = this.model.getChildAt(cell, i);
      const geo = this.getCellGeometry(child);
      if (geo?.relative) return false;
    }
    return childCount > 0;
  };

  // Overrides for move behavior
  graph.translateCell = function (cell, dx, dy) {
    const rel = getRelativePosition(
      this.view.getState(cell),
      dx * graph.view.scale,
      dy * graph.view.scale
    );
    if (rel) {
      let geo = this.model.getGeometry(cell);
      if (geo?.relative) {
        geo = geo.clone();
        geo.x = rel.x;
        geo.y = rel.y;
        this.model.setGeometry(cell, geo);
      }
    } else {
      mxGraph.prototype.translateCell.apply(this, arguments);
    }
  };

  graph.graphHandler.getDelta = function (me) {
    const point = mxUtils.convertPoint(
      this.graph.container,
      me.getX(),
      me.getY()
    );
    let delta = new mxPoint(point.x - this.first.x, point.y - this.first.y);

    if (this.cells?.length > 0 && this.cells[0]) {
      const state = this.graph.view.getState(this.cells[0]);
      const rel = getRelativePosition(state, delta.x, delta.y);
      const pstate = this.graph.view.getState(
        this.graph.model.getParent(state.cell)
      );

      if (rel && pstate) {
        delta = new mxPoint(
          pstate.x + pstate.width * rel.x - state.getCenterX(),
          pstate.y + pstate.height * rel.y - state.getCenterY()
        );
      }
    }
    return delta;
  };

  graph.graphHandler.shouldRemoveCellsFromParent = function (
    parent,
    cells,
    evt
  ) {
    return (
      cells.length === 0 &&
      !cells[0].geometry.relative &&
      mxGraphHandler.prototype.shouldRemoveCellsFromParent.apply(
        this,
        arguments
      )
    );
  };

  graph.isCellLocked = () => false;

  // Utility for relative dragging
  function getRelativePosition(state, dx, dy) {
    if (!state) return null;
    const model = graph.getModel();
    const geo = model.getGeometry(state.cell);
    if (!geo?.relative || model.isEdge(state.cell)) return null;

    const parent = model.getParent(state.cell);
    if (!model.isVertex(parent)) return null;

    const pstate = graph.view.getState(parent);
    if (!pstate) return null;

    let x = state.x + dx;
    let y = state.y + dy;
    const scale = graph.view.scale;

    if (geo.offset) {
      x -= geo.offset.x * scale;
      y -= geo.offset.y * scale;
    }

    x = (x - pstate.x) / pstate.width;
    y = (y - pstate.y) / pstate.height;

    const position = state.cell.position;
    if (typeof position === "string") {
      if (position.startsWith("left")) {
        x = 0;
        y = Math.min(1, Math.max(0, y));
      } else if (position.startsWith("right")) {
        x = 1;
        y = Math.min(1, Math.max(0, y));
      } else if (position.startsWith("top")) {
        x = Math.min(1, Math.max(0, x));
        y = 0;
      } else if (position.startsWith("bottom")) {
        x = Math.min(1, Math.max(0, x));
        y = 1;
      } else {
        // Fallback snap logic
        if (Math.abs(y - 0.5) <= Math.abs((x - 0.5) / 2)) {
          x = x > 0.5 ? 1 : 0;
          y = Math.min(1, Math.max(0, y));
        } else {
          x = Math.min(1, Math.max(0, x));
          y = y > 0.5 ? 1 : 0;
        }
      }
    }

    return new mxPoint(x, y);
  }

  // Red selection border instead of the default green that blends in.
  const origCreateSelectionShape = mxVertexHandler.prototype.createSelectionShape;
  mxVertexHandler.prototype.createSelectionShape = function (bounds) {
    const shape = origCreateSelectionShape.apply(this, arguments);
    const cellStyle = this.graph.model.getStyle(this.state.cell) || '';
    if (cellStyle.includes('shape=line')) {
      shape.stroke = '#ee1212';
    }
    return shape;
  };
};

// Utility to size symbol
const calculateSymbolRectSize = (
  config,
  stubSize = 20,
  minWidth = 125,
  minHeight = 70
) => {
  // Initialize counts with 0 for all sides
  const counts = { left: 0, right: 0, top: 0, bottom: 0 };

  // Sum pins per position using a simple loop
  config.forEach((pin) => {
    if (counts.hasOwnProperty(pin.position)) {
      counts[pin.position] += pin.pins.length;
    }
  });

  const width =
    Math.max(minWidth, Math.max(counts.left, counts.right) * stubSize);
  const height =
    Math.max(minHeight, Math.max(counts.top, counts.bottom) * stubSize);

  return { width, height };
};

function addRectAndPins(container, graph, pinConfig = [], symbolName = 'Symbol') {
      if(!(pinConfig.length > 0)) return;

      graphPropertiesForSymbol(container, graph);
      const parent = graph.getDefaultParent();

      const { width, height } = calculateSymbolRectSize(pinConfig);
      const rect = graph.insertVertex(parent, null, symbolName, 100, 100, width, height, 'shape=rectangle;resizable=1');

      const directions = {
        top:    { dx: 0, y: 0, dir: 'north', offsetY: -50 },
        bottom: { dx: 0, y: 1, dir: 'south', offsetY: 0 },
        left:   { dx: 0, x: 0, dir: 'west', offsetX: -50 },
        right:  { dx: 0, x: 1, dir: 'east', offsetX: 0 }
      };

      const baseStyle = 'shape=line;resizable=0;strokeColor=#00cc66;strokeWidth=1;';

      pinConfig.forEach(pinGroup => {
        const { position, pins } = pinGroup;
        const noOfPins = pins.length;
        if (noOfPins === 0) return;
        const isVertical = position === 'top' || position === 'bottom';
        const d = directions[position];
        const dx = 1 / (noOfPins + 1);

        for (let i = 0; i < noOfPins; i++) {
          const pin = pins[i];
          const name = pin.name || '';
          const type = pin.type || '';
          const x = isVertical ? dx * (i + 1) : d.x;
          const y = isVertical ? d.y : dx * (i + 1);
          const offset = isVertical ? new mxPoint(0, d.offsetY) : new mxPoint(d.offsetX, 0);

          // ✅ Ternary style selector
          const style =
          baseStyle +
          `direction=${d.dir};` +
          (position === 'top'
            ? 'verticalLabelPosition=bottom;verticalAlign=top;labelAngle=180;'
            : position === 'bottom'
            ? 'verticalLabelPosition=top;verticalAlign=bottom;'
            : position === 'right'
            ? 'labelPosition=left;align=right;'
            : position === 'left'
            ? 'labelPosition=right;align=left;'
            : '');
          
          let label = name.slice(0,4);

          if (position === 'top' || position === 'bottom') {
            const margin = position === 'top' ? 'margin-top: 5px;' : 'margin-bottom: 5px;';
            label =
              `<div title="${name}" style="display:inline-block; transform:rotate(-90deg); white-space:nowrap; text-align:right; ${margin}">` +
              label +
              '</div>';
          }else {
            // For left and right, show short label but include tooltip
            label =
              `<div title="${name}" style="white-space:nowrap;">${label}</div>`;
          }

          const stub = graph.insertVertex(
            rect,
            null,
            label,
            x,
            y,
            isVertical ? 1 : 50,
            isVertical ? 50 : 1,
            style
          );

          stub.geometry.offset = offset;
          stub.geometry.relative = true;

          // Add position property here
          stub.position = position;
          stub.name = name;
          stub.pinType = type;
        }
      });
    }

function editRectAndPins(container, graph, rect, pins = [], symbolName = 'Symbol') {
  // console.log("rect",rect, pins, symbolName);
  if (!pins.length) return;

  graphPropertiesForSymbol(container, graph);
  const parent = graph.getDefaultParent();

  const { x, y, width, height } = rect || { x: 100, y: 100, width: 150, height: 70 };
  const mainRect = graph.insertVertex(parent, null, symbolName, x, y, width, height, 'shape=rectangle;resizable=1');

  const baseStyle = 'shape=line;resizable=0;strokeColor=#00cc66;strokeWidth=1;';
  const directions = {
    top:    { y: 0, dir: 'north', offsetY: -50 },
    bottom: { y: 1, dir: 'south', offsetY: 0 },
    left:   { x: 0, dir: 'west', offsetX: -50 },
    right:  { x: 1, dir: 'east', offsetX: 0 }
  };

  for (const pin of pins) {
    const { name, type, position, value } = pin;
    const d = directions[position];
    if (!d) continue;

    const isVertical = position === 'top' || position === 'bottom';
    const x = isVertical ? value : d.x;
    const y = isVertical ? d.y : value;
    const offset = isVertical ? new mxPoint(0, d.offsetY) : new mxPoint(d.offsetX, 0);

    const style =
      baseStyle +
      `direction=${d.dir};` +
      (position === 'top'
        ? 'verticalLabelPosition=bottom;verticalAlign=top;labelAngle=180;'
        : position === 'bottom'
        ? 'verticalLabelPosition=top;verticalAlign=bottom;'
        : position === 'right'
        ? 'labelPosition=left;align=right;'
        : position === 'left'
        ? 'labelPosition=right;align=left;'
        : '');

    let label = name.slice(0, 4);
    if (isVertical) {
      const margin = position === 'top' ? 'margin-top: 5px;' : 'margin-bottom: 5px;';
      label =
        `<div title="${name}" style="display:inline-block; transform:rotate(-90deg); white-space:nowrap; text-align:right; ${margin}">${label}</div>`;
    } else {
      label = `<div title="${name}" style="white-space:nowrap;">${label}</div>`;
    }

    const stub = graph.insertVertex(
      mainRect,
      null,
      label,
      x,
      y,
      isVertical ? 1 : 50,
      isVertical ? 50 : 1,
      style
    );

    stub.geometry.offset = offset;
    stub.geometry.relative = true;
    stub.position = position;
    stub.name = name;
    stub.pinType = type;
  }
}

export { addRectAndPins, editRectAndPins };
